<template>
  <div>
    <TitlePage title="Statistiche utente"></TitlePage>

    <div class="mb-4">
      <v-btn text small to="/customers" class="mr-2">
        <v-icon left small>mdi-arrow-left</v-icon>
        Utenti
      </v-btn>
      <v-btn text small to="/match-stats/leaderboard">
        <v-icon left small>mdi-format-list-numbered</v-icon>
        Classifiche
      </v-btn>
    </div>

    <v-card outlined class="pa-4 mb-4">
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon left color="warning">mdi-account-edit</v-icon>
        Selezione utente e sport
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="7">
            <v-autocomplete
              v-model="selectedUser"
              :items="userItems"
              :search-input.sync="userSearch"
              :loading="searchingUsers"
              :disabled="Boolean(queryUserId)"
              item-text="label"
              return-object
              label="Cerca utente per nome, cognome, email o telefono"
              dense
              outlined
              hide-details
              clearable
              no-filter
              @change="onUserChange"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-select
              v-model="sportMaster"
              :items="sportItems"
              item-text="text"
              item-value="value"
              label="Sport master"
              dense
              outlined
              hide-details
              clearable
              @change="loadCurrentStats"
            />
          </v-col>
        </v-row>

        <div v-if="userId" class="caption mt-3 text--secondary">
          Utente selezionato: <strong>{{ userLabel }}</strong>
        </div>
      </v-card-text>
    </v-card>

    <v-card outlined class="pa-4" :disabled="!canEdit">
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon left color="primary">mdi-chart-bar</v-icon>
        Dati statistici e classifica
      </v-card-title>
      <v-card-subtitle>
        I campi PG, MVP, V, P, S, PT e posizione aggiornano la riga classifica salvata. Le statistiche sport-specifiche aggiornano anche le statistiche utente.
      </v-card-subtitle>

      <v-card-text>
        <v-row>
          <v-col
            v-for="field in summaryFields"
            :key="field.value"
            cols="6"
            md="3"
          >
            <v-text-field
              v-model.number="form[field.value]"
              :label="field.text"
              dense
              outlined
              hide-details
              type="number"
              min="0"
            />
          </v-col>
        </v-row>

        <div class="subtitle-2 font-weight-bold mt-5 mb-2">Statistiche sport</div>
        <v-alert v-if="!statFields.length" dense text type="info">
          Nessuna statistica configurata per questo sport.
        </v-alert>
        <v-row v-else>
          <v-col
            v-for="field in statFields"
            :key="field.key"
            cols="6"
            md="3"
          >
            <v-text-field
              v-model.number="form.stats[field.key]"
              :label="field.label"
              dense
              outlined
              hide-details
              type="number"
              min="0"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          depressed
          color="warning"
          :loading="saving"
          :disabled="!canEdit"
          @click="submitOverride"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salva statistiche
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Chiudi</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import TitlePage from '@/components/utils/TitlePage';

const FIXED_STAT_KEYS = new Set([
  'matches',
  'matches_played',
  'pg',
  'mvp',
  'mvp_received',
  'wins',
  'win',
  'draws',
  'draw',
  'losses',
  'loss',
  'score',
  'points',
  'rank',
]);

export default {
  name: 'UserStatsOverride',
  components: { TitlePage },
  data() {
    return {
      queryUserId: '',
      userId: '',
      selectedUser: null,
      userSearch: '',
      userItems: [],
      searchingUsers: false,
      sportMaster: '',
      sportConfigs: [],
      leaderboardRow: null,
      saving: false,
      form: this.emptyForm(),
      summaryFields: [
        { text: 'Posizione', value: 'rank' },
        { text: 'PT', value: 'score' },
        { text: 'PG', value: 'matches_played' },
        { text: 'MVP', value: 'mvp_received' },
        { text: 'V', value: 'wins' },
        { text: 'P', value: 'draws' },
        { text: 'S', value: 'losses' },
      ],
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    canEdit() {
      return Boolean(this.userId && this.sportMaster);
    },
    userLabel() {
      return this.selectedUser ? this.selectedUser.label : this.userId;
    },
    sportItems() {
      return this.sportConfigs
        .filter(item => item && item.sport_master && item.active !== false)
        .map(item => ({ text: item.sport_master, value: item.sport_master }));
    },
    selectedSportConfig() {
      return this.sportConfigs.find(item => item.sport_master === this.sportMaster) || null;
    },
    statFields() {
      return ((this.selectedSportConfig && this.selectedSportConfig.stats) || [])
        .filter(field => field && field.key && field.active !== false && !FIXED_STAT_KEYS.has(String(field.key).toLowerCase()))
        .map(field => ({ key: field.key, label: field.label || field.key }));
    },
  },
  watch: {
    userSearch(value) {
      this.debouncedSearchUsers(value);
    },
  },
  async created() {
    this.debouncedSearchUsers = this.debounce(this.searchUsers, 350);
    this.queryUserId = this.$route.query.user_id || '';
    await this.loadSports();
    if (this.queryUserId) {
      await this.loadUserById(this.queryUserId);
    }
  },
  methods: {
    ...mapActions({
      searchCustomers: 'customers/search',
      getCustomer: 'customers/edit',
      getSportConfigs: 'sportMasterConfig/index',
      adminGetLeaderboard: 'matchStats/adminGetLeaderboard',
      adminOverrideUserStats: 'matchStats/adminOverrideUserStats',
      adminUpsertUserLeaderboard: 'matchStats/adminUpsertUserLeaderboard',
    }),
    emptyForm() {
      return {
        rank: 0,
        score: 0,
        matches_played: 0,
        mvp_received: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        stats: {},
      };
    },
    debounce(fn, wait) {
      let t = null;
      return function debounced() {
        const ctx = this;
        const args = arguments;
        clearTimeout(t);
        t = setTimeout(function run() { fn.apply(ctx, args); }, wait);
      };
    },
    async loadSports() {
      const result = await this.getSportConfigs({ active: true });
      this.sportConfigs = Array.isArray(result) ? result : [];
    },
    async loadUserById(id) {
      const result = await this.getCustomer({ id });
      const user = this.normalizeUser(result && (result.result || result));
      this.selectedUser = user || { _id: id, label: id };
      this.userItems = [this.selectedUser];
      this.userId = id;
    },
    async searchUsers(value) {
      if (this.queryUserId || !value || value.length < 2) return;
      this.searchingUsers = true;
      try {
        const result = await this.searchCustomers({ search: value });
        this.userItems = (Array.isArray(result) ? result : []).map(this.normalizeUser).filter(Boolean);
      } finally {
        this.searchingUsers = false;
      }
    },
    normalizeUser(user) {
      if (!user) return null;
      const name = [user.name, user.surname].filter(Boolean).join(' ');
      const contacts = [user.email, user.phone].filter(Boolean).join(' - ');
      return {
        ...user,
        label: [name || 'Utente', contacts].filter(Boolean).join(' | '),
      };
    },
    async onUserChange(user) {
      this.userId = user && user._id ? user._id : '';
      await this.loadCurrentStats();
    },
    ensureSportStatKeys() {
      for (const field of this.statFields) {
        if (this.form.stats[field.key] === undefined) {
          this.$set(this.form.stats, field.key, 0);
        }
      }
    },
    async loadCurrentStats() {
      this.form = this.emptyForm();
      this.leaderboardRow = null;
      this.ensureSportStatKeys();
      if (!this.canEdit) return;

      const result = await this.adminGetLeaderboard({
        user_id: this.userId,
        sport_master: this.sportMaster,
        limit: 1,
      });
      const row = result && result.rows && result.rows.length ? result.rows[0] : null;
      this.leaderboardRow = row;
      if (row) {
        this.form = {
          rank: row.rank || 0,
          score: row.score || 0,
          matches_played: row.matches_played || 0,
          mvp_received: row.mvp_received || 0,
          wins: row.wins || 0,
          draws: row.draws || 0,
          losses: row.losses || 0,
          stats: { ...(row.stats || {}) },
        };
      }
      this.ensureSportStatKeys();
    },
    async submitOverride() {
      if (!this.canEdit) return;
      this.saving = true;
      try {
        const payload = {
          sport_master: this.sportMaster,
          rank: this.form.rank,
          score: this.form.score,
          matches_played: this.form.matches_played,
          mvp_received: this.form.mvp_received,
          wins: this.form.wins,
          draws: this.form.draws,
          losses: this.form.losses,
          stats: this.form.stats,
        };

        await this.adminOverrideUserStats({
          userId: this.userId,
          sport_master: this.sportMaster,
          stats: this.form.stats,
        });
        await this.adminUpsertUserLeaderboard({
          userId: this.userId,
          payload,
        });
        this.toast('Statistiche utente aggiornate', 'success');
        await this.loadCurrentStats();
      } catch {
        this.toast('Errore durante il salvataggio', 'error');
      } finally {
        this.saving = false;
      }
    },
    toast(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>
